import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Paper,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Fab,
  Tooltip
} from '@mui/material';
import {
  TrendingUp,
  Refresh,
  AutoAwesome,
  Schedule,
  Share,
  ArrowBack
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { contentAPI } from '../services/contentAPI';
import { TrendingTopic, TrendingTopicsRequest } from '../types/api';
import { useDebounce } from '../hooks/useDebounce';

interface TrendingDomain {
  value: string;
  label: string;
}

export const TrendingPage: React.FC = () => {
  const navigate = useNavigate();
  const [topics, setTopics] = useState<TrendingTopic[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedDomain, setSelectedDomain] = useState<string>('all');
  const [domains, setDomains] = useState<TrendingDomain[]>([]);
  const [maxResults, setMaxResults] = useState<number>(12);
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchMode, setSearchMode] = useState<boolean>(false);
  const [generateLoading, setGenerateLoading] = useState<string | null>(null);

  // Debounce search query with 800ms delay
  const debouncedSearchQuery = useDebounce(searchQuery, 800);

  useEffect(() => {
    fetchTrendingDomains();
    fetchTrendingTopics();
  }, []);

  // Trigger fetch when selectedDomain changes
  useEffect(() => {
    if (!searchMode && selectedDomain) {
      console.log('Fetching topics for domain:', selectedDomain);
      fetchTrendingTopics();
    }
  }, [selectedDomain, maxResults, searchMode]); // Add searchMode to dependencies

  // Handle debounced search
  useEffect(() => {
    if (searchMode && debouncedSearchQuery.trim()) {
      console.log('Performing search for:', debouncedSearchQuery);
      fetchTrendingTopics();
    } else if (searchMode && !debouncedSearchQuery.trim()) {
      // If search is cleared, go back to domain mode
      setSearchMode(false);
      fetchTrendingTopics();
    }
  }, [debouncedSearchQuery, searchMode]);

  const fetchTrendingDomains = async () => {
    try {
      const response = await contentAPI.getTrendingDomains();
      setDomains(response.domains || []);
    } catch (err) {
      console.error('Failed to fetch trending domains:', err);
      // Minimal fallback
      setDomains([{ value: 'all', label: 'All' }]);
    }
  };

  const fetchTrendingTopics = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let response;
      
      if (searchMode && debouncedSearchQuery.trim()) {
        // Search mode
        response = await contentAPI.searchTrendingTopics(debouncedSearchQuery.trim(), maxResults);
      } else {
        // Domain-based browsing
        response = await contentAPI.getTrendingTopicsByDomain(selectedDomain, maxResults);
      }
      
      if (response.data) {
        setTopics(response.data);
        setError(null);
      } else {
        setTopics([]);
        setError('No trending topics found');
      }
    } catch (err: any) {
      console.error('Failed to fetch trending topics:', err);
      setError(`Failed to fetch trending topics: ${err.response?.data?.detail || err.message}`);
      setTopics([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (query: string) => {
    setSearchQuery(query);
    setSearchMode(!!query.trim());
    // Don't fetch immediately - let debounce handle it
  };

  const handleDomainChange = (domain: string) => {
    console.log('Domain changed from', selectedDomain, 'to', domain);
    setSelectedDomain(domain);
    setSearchMode(false);
    setSearchQuery('');
    // Use useEffect to trigger fetch when selectedDomain changes
  };

  const handleGeneratePost = async (topic: TrendingTopic) => {
    try {
      setGenerateLoading(topic.title);
      
      // Generate post content using the contentAPI (without saving)
      const generateRequest = {
        topic: topic.title,
        topic_details: [topic],
        use_topic_as_reference_only: true
      };
      
      const response = await contentAPI.generatePost(generateRequest);
      
      if (response.data) {
        // Navigate to create post page with generated content
        navigate('/create-post', { 
          state: { 
            topic: topic.title,
            generatedContent: response.data.content,
            topicDetails: [topic],
            imageSearchTerms: response.data.image_search_terms,
            fullGeneratedPost: response.data // Pass the full generated post object
          } 
        });
      }
    } catch (err: any) {
      console.error('Failed to generate post:', err);
      setError(`Failed to generate post: ${err.response?.data?.detail || err.message}`);
    } finally {
      setGenerateLoading(null);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/dashboard')}
            sx={{ mr: 2 }}
          >
            Back to Dashboard
          </Button>
          <TrendingUp color="primary" sx={{ mr: 2, fontSize: 32 }} />
          <Typography variant="h4" component="h1">
            Trending Topics
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Discover what's trending across different domains and generate content from hot topics
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          📡 Real-time Sources: Reddit, HackerNews, GitHub, NewsAPI
        </Typography>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          {searchMode ? 'Search Trending Topics' : 'Browse by Domain'}
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          {/* Search Bar */}
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Search trending topics by keyword..."
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder="e.g., artificial intelligence, climate change, gaming"
              InputProps={{
                startAdornment: <Box sx={{ mr: 1 }}>🔍</Box>
              }}
              variant="outlined"
            />
          </Grid>
          
          {/* Domain Filter (only when not searching) */}
          {!searchMode && (
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Domain</InputLabel>
                <Select
                  value={selectedDomain}
                  label="Domain"
                  onChange={(e) => handleDomainChange(e.target.value)}
                >
                  {domains.map(domain => (
                    <MenuItem key={domain.value} value={domain.value}>
                      {domain.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          
          {/* Max Results */}
          <Grid item xs={6} md={2}>
            <TextField
              fullWidth
              label="Results"
              type="number"
              value={maxResults}
              onChange={(e) => {
                const val = parseInt(e.target.value) || 12;
                setMaxResults(Math.min(Math.max(val, 1), 50));
              }}
              inputProps={{ min: 1, max: 50 }}
              size="small"
            />
          </Grid>
          
          {/* Refresh Button */}
          <Grid item xs={6} md={1}>
            <Button
              variant="contained"
              onClick={fetchTrendingTopics}
              disabled={loading}
              fullWidth
              sx={{ height: '40px' }}
            >
              {loading ? <CircularProgress size={20} /> : <Refresh />}
            </Button>
          </Grid>
        </Grid>
        
        {/* Status Info */}
        <Box sx={{ mt: 2, p: 2, bgcolor: searchMode ? 'success.50' : 'primary.50', borderRadius: 1 }}>
          <Typography variant="body2" color={searchMode ? 'success.main' : 'primary.main'}>
            {searchMode ? (
              <>🔍 {searchQuery !== debouncedSearchQuery ? 'Typing...' : `Searching for: "${debouncedSearchQuery}"`} across all sources</>
            ) : (
              <>📊 Showing trending topics for: <strong>{domains.find(d => d.value === selectedDomain)?.label || selectedDomain}</strong> domain</>
            )}
            <br />
            <small>Real-time Sources: Reddit, NewsAPI, HackerNews, GitHub{searchMode && searchQuery !== debouncedSearchQuery ? ' • Search will update in a moment...' : ''}</small>
          </Typography>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Trending Topics */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Current Trends ({topics.length})
        </Typography>
        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : topics.length > 0 ? (
          <Grid container spacing={3}>
            {topics.map((topic, index) => (
              <Grid item xs={12} md={6} key={index}>
                <Card 
                  sx={{ 
                    height: '100%',
                    cursor: 'pointer',
                    transition: 'transform 0.2s, box-shadow 0.2s',
                    '&:hover': { 
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    }
                  }}
                  onClick={() => {/* Optional: Add topic details modal */}}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'start', mb: 2 }}>
                      <TrendingUp color="success" sx={{ mr: 1, mt: 0.5 }} />
                      <Typography variant="h6" component="h3" sx={{ flex: 1 }}>
                        {topic.title}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="body2" 
                      color="text.secondary" 
                      sx={{ 
                        mb: 2,
                        display: '-webkit-box',
                        WebkitLineClamp: 3,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden'
                      }}
                    >
                      {topic.body}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                      {topic.source && (
                        <Chip 
                          label={topic.source} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                      )}
                      {topic.published_date && (
                        <Typography variant="caption" color="text.secondary">
                          {new Date(topic.published_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>
                    
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={generateLoading === topic.title ? <CircularProgress size={16} /> : <AutoAwesome />}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleGeneratePost(topic);
                        }}
                        disabled={!!generateLoading}
                      >
                        {generateLoading === topic.title ? 'Generating...' : 'Generate Post'}
                      </Button>
                      {topic.url && (
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Share />}
                          onClick={(e) => {
                            e.stopPropagation();
                            window.open(topic.url, '_blank');
                          }}
                        >
                          Read More
                        </Button>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <TrendingUp sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No trending topics found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Try adjusting your filters or check back later
            </Typography>
            <Button variant="contained" onClick={fetchTrendingTopics}>
              Refresh Trends
            </Button>
          </Paper>
        )}
      </Box>

      {/* Floating Action Button */}
      <Tooltip title="Create Post">
        <Fab
          color="primary"
          aria-label="create"
          sx={{
            position: 'fixed',
            bottom: 24,
            right: 24,
          }}
          onClick={() => navigate('/create-post')}
        >
          <AutoAwesome />
        </Fab>
      </Tooltip>
    </Container>
  );
};

export default TrendingPage; 